import React, { useState, useEffect } from 'react';
import s3Service from '../services/s3.service';

/**
 * S3Image Component
 * Automatically fetches signed URL for private S3 images
 *
 * Usage:
 * <S3Image
 *   src="https://bucket.s3.region.amazonaws.com/members/photo.jpg"
 *   alt="Member Photo"
 *   className="w-32 h-32 rounded-full"
 *   fallback={<div>Initials</div>}
 * />
 */
const S3Image = ({ src, alt, className = '', fallback = null, ...props }) => {
  const [signedUrl, setSignedUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const loadSignedUrl = async () => {
      if (!src) {
        setLoading(false);
        setError(true);
        return;
      }

      // Check if it's already a local or signed URL
      if (src.startsWith('http://localhost') || src.startsWith('blob:') || src.includes('X-Amz-Signature')) {
        setSignedUrl(src);
        setLoading(false);
        return;
      }

      // It's an S3 URL, get signed URL
      try {
        setLoading(true);
        setError(false);
        const url = await s3Service.getSignedUrl(src);
        setSignedUrl(url);
      } catch (err) {
        console.error('Failed to load signed URL:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    loadSignedUrl();
  }, [src]);

  // Show fallback if no src, error, or loading
  if (!src || error) {
    return fallback || (
      <div className={`bg-gray-200 flex items-center justify-center ${className}`}>
        <span className="text-gray-400">No Image</span>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={`bg-gray-100 flex items-center justify-center ${className}`}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <img
      src={signedUrl}
      alt={alt}
      className={className}
      onError={() => setError(true)}
      {...props}
    />
  );
};

export default S3Image;
